




































import { mixins } from 'vue-class-component'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import CarrierFormInfo from '@/modules/Carrier/components/forms/CarrierFormInfo.vue'
import CarrierFormAdditional from '@/modules/Carrier/components/forms/CarrierFormAdditional.vue'
import CarrierFormContact from '@/modules/Carrier/components/forms/CarrierFormContact.vue'
import RecordTypesHandler from '@/mixins/record-types'
import { newCarrier, editCarrier } from '@/modules/Carrier/gql/carrier.gql'
import { ICarrierInfoInput, ICarrierContactInput, ICarrierAdditionalInput } from '@/modules/Carrier/interfaces/carrier-form'
import { Carrier } from '@/modules/Carrier/interfaces/carrier'
import { documentDeliveryKeys } from '@/modules/Carrier/helpers/carrier'
import { states } from '@/utils/us_states'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    CarrierFormInfo,
    AppDialog,
    CarrierFormAdditional,
    AppSteps,
    CarrierFormContact
  }
})
export default class CarrierForm extends mixins(RecordTypesHandler) {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => Carrier }) readonly carrier!: Carrier

  showDialog = false
  loading = false

  stepIndex = 0
  steps = [
    ['Carrier Info', 770, 621],
    ['Contact Info', 770, 511],
    ['Additional Info', 770, 361]
  ]

  infoInput: ICarrierInfoInput = {
    carrierName: '',
    website: '',
    hoursOfOperation: '',
    username: '',
    policyTypes: '',
    password: '',
    amBestRating: '',
    producerCode: '',
    notes: '',
    directAppointment: false
  }

  contactInput: ICarrierContactInput = {
    street1: '',
    street2: '',
    stateIdx: -1,
    city: '',
    phone: '',
    fax: '',
    zipCode: '',
    email: ''
  }

  additionalInput: ICarrierAdditionalInput = {
    policyDocDeliveryIdx: -1,
    endorsementDocDeliveryIdx: -1,
    downloadsIdx: -1
  }

  downloadsOptions = ['YES', 'NO']

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.carrier
        ? 'Save'
        : 'Create'
  }

  get confirmAheadText () {
    return this.carrier ? 'Save' : 'Create'
  }

  get canPost () {
    return this.stepIndex < this.steps.length - 1
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.carrier) {
      const {
        name, website, hoursOfOperation, username,
        policyTypes, password, amBestRating,
        producerCode, notes, directAppointment,
        primaryAddressStreet1, primaryAddressStreet2,
        primaryAddressState, primaryAddressCity, primaryAddressZipCode,
        email, policyDocumentsDelivery, endorsementDocumentDelivery, downloads,
        phone, countryCode, extensionNumber, countryCallingCode,
        fax, faxCountryCode, faxExtensionNumber, faxCountryCallingCode
      } = this.carrier

      this.infoInput = {
        carrierName: name,
        website,
        hoursOfOperation,
        username,
        policyTypes,
        password,
        amBestRating,
        producerCode,
        notes,
        directAppointment: directAppointment || false
      }
      this.contactInput = {
        street1: primaryAddressStreet1,
        street2: primaryAddressStreet2,
        stateIdx: states.findIndex(state => primaryAddressState === state),
        city: primaryAddressCity,
        phone: (countryCode || '') + ',' + (phone || '') + ',' + (extensionNumber || '') + ',' + (countryCallingCode || ''),
        fax: (faxCountryCode || '') + ',' + (fax || '') + ',' + (faxExtensionNumber || '') + ',' + (faxCountryCallingCode || ''),
        zipCode: primaryAddressZipCode,
        email
      }
      this.additionalInput = {
        policyDocDeliveryIdx: documentDeliveryKeys.findIndex(key => key === policyDocumentsDelivery),
        endorsementDocDeliveryIdx: documentDeliveryKeys.findIndex(key => key === endorsementDocumentDelivery),
        downloadsIdx: this.downloadsOptions.findIndex(option => option === downloads)
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.carrier) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.carrier) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    }
  }

  async handleCreate () {
    const {
      infoInput: {
        carrierName, website, hoursOfOperation, username, policyTypes, password, amBestRating, producerCode, notes, directAppointment
      },
      contactInput: {
        street1, street2, stateIdx, city, phone, fax, zipCode, email
      },
      additionalInput: {
        policyDocDeliveryIdx, endorsementDocDeliveryIdx, downloadsIdx
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: newCarrier,
        variables: {
          carrierInput: {
            name: carrierName,
            hoursOfOperation,
            policyTypes,
            amBestRating,
            notes,
            website,
            username,
            password,
            producerCode,
            directAppointment: directAppointment || false,
            email,
            phone: delNonNumCode(phoneArr[1] || ''),
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            fax: fax ? delNonNumCode(faxArr[1]) : null,
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || '',
            primaryAddressStreet1: street1,
            primaryAddressStreet2: street2,
            primaryAddressState: states[stateIdx] || null,
            primaryAddressCity: city,
            primaryAddressZipCode: zipCode,
            policyDocumentsDelivery: documentDeliveryKeys[policyDocDeliveryIdx] || null,
            downloads: this.downloadsOptions[downloadsIdx] || null,
            endorsementDocumentDelivery: documentDeliveryKeys[endorsementDocDeliveryIdx] || null
          }
        }
      })
      this.clearForm()
      this.onSuccess('Create the new carrier successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      infoInput: {
        carrierName, website, hoursOfOperation, username, policyTypes, password, amBestRating, producerCode, notes, directAppointment
      },
      contactInput: {
        street1, street2, stateIdx, city, phone, fax, zipCode, email
      },
      additionalInput: {
        policyDocDeliveryIdx, endorsementDocDeliveryIdx, downloadsIdx
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: editCarrier,
        variables: {
          carrierId: +this.carrier.id,
          carrierInput: {
            name: carrierName,
            hoursOfOperation,
            policyTypes,
            amBestRating,
            notes,
            website,
            username,
            password,
            producerCode,
            directAppointment: directAppointment || false,
            email,
            phone: delNonNumCode(phoneArr[1] || ''),
            extensionNumber: phoneArr[2] || '',
            countryCode: phoneArr[0] || '',
            countryCallingCode: phoneArr[3] || '',
            fax: fax ? delNonNumCode(faxArr[1]) : null,
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || '',
            primaryAddressStreet1: street1,
            primaryAddressStreet2: street2,
            primaryAddressState: states[stateIdx] || null,
            primaryAddressCity: city,
            primaryAddressZipCode: zipCode,
            policyDocumentsDelivery: documentDeliveryKeys[policyDocDeliveryIdx] || null,
            downloads: this.downloadsOptions[downloadsIdx] || null,
            endorsementDocumentDelivery: documentDeliveryKeys[endorsementDocDeliveryIdx] || null
          }
        }
      })
      this.clearForm()
      this.onSuccess('Update carrier successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      carrierName: '',
      website: '',
      hoursOfOperation: '',
      username: '',
      policyTypes: '',
      password: '',
      amBestRating: '',
      producerCode: '',
      notes: '',
      directAppointment: false
    }
    this.contactInput = {
      street1: '',
      street2: '',
      stateIdx: -1,
      city: '',
      phone: '',
      fax: '',
      zipCode: '',
      email: ''
    }
    this.additionalInput = {
      policyDocDeliveryIdx: -1,
      endorsementDocDeliveryIdx: -1,
      downloadsIdx: -1
    }
  }

  @Emit('hide')
  handleHide () {
    this.$emit('input', false)
  }

  @Emit('cancel')
  handleCancel () {
    this.handleHide()
    this.clearForm()
  }

  @Emit('success')
  @Emit('hide')
  onSuccess (msg = '') {
    this.handleHide()
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
